import {useSiteLanguage} from "../career/SiteLanguage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "../styles/EvrakPopup.css";
import {asArray,useCareerLanguage} from '../career/CareerLanguage';
import {catalogLabel} from '../career/CareerCatalog';
import {DOCUMENT_ACCEPT,DOCUMENT_UPLOAD_TIMEOUT,validateDocument,documentUploadError,uploadProgress,hasNewDocumentFile} from '../career/documentUpload';
import {AlertCircle,CheckCircle2} from 'lucide-react';

const EvrakPopup = ({ onClose, inline=false }) => {
  const {tx} = useSiteLanguage();
  const {t,lang}=useCareerLanguage();
  const [evraklar, setEvraklar] = useState([]);
  const [detayEvrak, setDetayEvrak] = useState(null);
  const token = localStorage.getItem("token");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading,setLoading]=useState(true),[loadError,setLoadError]=useState(false);
  const [progress,setProgress]=useState(0),[online,setOnline]=useState(navigator.onLine!==false);
  const [needsCheck,setNeedsCheck]=useState(false),[checking,setChecking]=useState(false);
  const uploadRef=useRef(null),checkRef=useRef(false),lastAttempt=useRef(null),mounted=useRef(true);
  const feedbackRef=useRef(null),listSequence=useRef(0),listController=useRef(null);

  const isMobile = window.innerWidth <= 600;

  const handleFileSelect = (e) => {
    if(uploadRef.current||checkRef.current)return;
    const file = e.target.files?.[0];
    if (file) {
      setPreviewName(file.name);
      const issue=validateDocument(file);
      setSelectedFile(issue?null:file);
      setFeedback(issue?{key:issue,type:'error'}:null);
    }

    e.target.value = ''; // Allow choosing the same file again, including on iOS.
  };

  const readDocuments=useCallback(async(signal)=>{
    const res=await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Evrak/evraklarim`,{
      headers:{Authorization:`Bearer ${token}`},timeout:30000,signal
    });
    return asArray(res.data);
  },[token]);

  const fetchEvraklar=useCallback(async()=>{
    const sequence=++listSequence.current;
    listController.current?.abort();
    const controller=new AbortController();listController.current=controller;
    setLoading(true);setLoadError(false);
    try{
      const list=await readDocuments(controller.signal);
      if(!mounted.current||sequence!==listSequence.current)return null;
      setEvraklar(list);return list;
    }catch{
      if(mounted.current&&sequence===listSequence.current)setLoadError(true);
      return null;
    }finally{
      if(mounted.current&&sequence===listSequence.current)setLoading(false);
    }
  },[readDocuments]);

  useEffect(()=>{
    mounted.current=true;
    fetchEvraklar();
    const offline=()=>{
      setOnline(false);
      if(uploadRef.current){uploadRef.current.reason='offline';uploadRef.current.controller.abort();}
    };
    const connected=()=>setOnline(true);
    window.addEventListener('offline',offline);window.addEventListener('online',connected);
    return()=>{
      mounted.current=false;
      listController.current?.abort();uploadRef.current?.controller.abort();
      window.removeEventListener('offline',offline);window.removeEventListener('online',connected);
    };
  },[fetchEvraklar]);
  useEffect(()=>{
    if(!feedback)return;
    feedbackRef.current?.focus({preventScroll:true});
    feedbackRef.current?.scrollIntoView?.({block:'nearest',behavior:'auto'});
  },[feedback]);
  useEffect(()=>{
    if(!isUploading)return;
    const leave=e=>{e.preventDefault();e.returnValue='';};
    window.addEventListener('beforeunload',leave);
    return()=>window.removeEventListener('beforeunload',leave);
  },[isUploading]);

  function closeDetail(){
    if(uploadRef.current||checkRef.current)return;
    setDetayEvrak(null);setSelectedFile(null);setPreviewName('');setNeedsCheck(false);setFeedback(null);lastAttempt.current=null;
  }
  async function uploaded(){
    // The write succeeded. A later list failure must never be reported as upload failure.
    setNeedsCheck(false);setSelectedFile(null);setPreviewName('');setDetayEvrak(null);
    setFeedback({key:'uploadSuccess',type:'success'});
    const id=lastAttempt.current?.evrakId;
    setEvraklar(list=>list.map(row=>row.evrakId===id?{...row,durum:'Yüklendi',yuklemeDurumu:'Yüklendi'}:row));
    const refreshed=await fetchEvraklar();
    if(mounted.current&&!refreshed)setFeedback({key:'uploadRefreshPending',type:'warning'});
  }
  async function checkStatus(){
    if(uploadRef.current||checkRef.current||!lastAttempt.current)return;
    checkRef.current=true;setChecking(true);
    try{
      const list=await readDocuments();
      if(!mounted.current)return;
      setEvraklar(list);
      const current=list.find(row=>row.evrakId===lastAttempt.current.evrakId);
      if(hasNewDocumentFile(lastAttempt.current,current))await uploaded();
      else{
        if(current)setDetayEvrak(current);
        setFeedback({key:'uploadNotConfirmed',type:'warning'});
      }
    }catch{
      if(mounted.current)setFeedback({key:'uploadCheckFailed',type:'error'});
    }finally{
      checkRef.current=false;if(mounted.current)setChecking(false);
    }
  }
  const handleUpload = async () => {
    if(uploadRef.current||checkRef.current||!detayEvrak)return;
    const issue=validateDocument(selectedFile);
    if(issue){setFeedback({key:issue,type:'error'});return;}
    if(navigator.onLine===false){setFeedback({key:'uploadOffline',type:'error'});return;}
    if(detayEvrak.durum?.toLowerCase()==='onaylandi'){setFeedback({key:'uploadApproved',type:'error'});return;}
    const task={controller:new AbortController(),reason:''};
    uploadRef.current=task;lastAttempt.current={...detayEvrak};
    setIsUploading(true);setProgress(0);setNeedsCheck(false);setFeedback(null);
    const formData = new FormData();
    formData.append("EvrakId", detayEvrak.evrakId);
    formData.append("Dosya", selectedFile);
    try {
      const response=await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/Evrak/evrakyukle`, formData, {
        headers:{Authorization:`Bearer ${token}`},
        timeout:DOCUMENT_UPLOAD_TIMEOUT,signal:task.controller.signal,
        onUploadProgress:event=>{if(mounted.current&&uploadRef.current===task)setProgress(uploadProgress(event));}
      });
      if(response.status!==200||response.data?.success===false){const rejected=new Error('uploadNotAcknowledged');rejected.response=response;throw rejected;}
      if(mounted.current&&uploadRef.current===task)await uploaded();
    } catch (err) {
      if(mounted.current&&uploadRef.current===task){
        const failure=documentUploadError(err,navigator.onLine!==false,task.reason);
        setNeedsCheck(failure.uncertain);setFeedback({key:failure.key,type:'error'});
      }
    } finally {
      if(uploadRef.current===task){uploadRef.current=null;if(mounted.current)setIsUploading(false);}
    }
  };

  const evrakOnaylandi = detayEvrak && detayEvrak.durum?.toLowerCase() === "onaylandi";

  return (
    <div className={inline?'career-documents':'modal-overlay'}>
      <div className={`evrak-popup${inline?' evrak-inline':''}`}>
        {!inline&&<button type="button" className="evrak-close-btn" disabled={isUploading||checking} onClick={onClose} aria-label={t('close')}>✖</button>}
        <h3 className="evrak-title">{tx("Gerekli Evraklar")}</h3>
        {feedback&&<div ref={feedbackRef} tabIndex={-1} role={feedback.type==='error'?'alert':'status'} className={`evrak-feedback evrak-feedback-${feedback.type}`}>
          {feedback.type==='success'?<CheckCircle2 size={21} aria-hidden="true"/>:<AlertCircle size={21} aria-hidden="true"/>}<p>{feedback.literal?tx(feedback.literal):t(feedback.key)}</p>
        </div>}
        {!online&&!isUploading&&!feedback&&<p className="evrak-network-note" role="status">{t('uploadOffline')}</p>}
        {loading&&<p role="status">{t('loading')}</p>}
        {loadError&&<div role="alert"><p>{t('error')}</p><button type="button" onClick={fetchEvraklar}>{t('retry')}</button></div>}

        {!detayEvrak && (
          <>
            {!isMobile && (
              <div className="evrak-header">
                <div className="evrak-col">{tx("Evrak Adı")}</div>
                <div className="evrak-col">{tx("Teslim Tarihi")}</div>
                <div className="evrak-col">{tx("Yükleme Durumu")}</div>
                <div className="evrak-col">{tx("İnceleme Durumu")}</div>
                <div className="evrak-col"></div>
              </div>
            )}
            <ul className="evrak-list">
              {evraklar.map((evrak, i) => (
                <li key={evrak.evrakId||i} className={`evrak-item ${isMobile ? "mobile-row" : "desktop-row"}`}>
                  <div className="evrak-col" data-label={tx("Evrak Adı :")}><strong>{catalogLabel('documentName',evrak.ad,lang)}</strong></div>
                  <div className="evrak-col" data-label={tx("Teslim Tarihi :")}>{new Date(evrak.sonTeslimTarihi).toLocaleDateString(lang)}</div>
                  <div className="evrak-col" data-label={tx("Yükleme Durumu :")}>
                    <span className={evrak.yuklemeDurumu === "Yüklendi" ? "evrak-durum-yes" : "evrak-durum-no"}>
                      {catalogLabel('documentStatus',evrak.yuklemeDurumu,lang)}
                    </span>
                  </div>
                  <div className="evrak-col" data-label={tx("İnceleme Durumu")}>
                    <span className={
                      evrak.durum === "Onaylandi" ? "evrak-durum-yes" :
                        evrak.durum === "Reddedildi" ? "evrak-durum-red" :
                          "evrak-durum-waiting"
                    }>
                      {catalogLabel('documentStatus',evrak.durum,lang)}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="evrak-detay-btn"
                    onClick={() => {
                      setDetayEvrak(evrak);setFeedback(null);setNeedsCheck(false);lastAttempt.current=null;
                    }}
                  >{tx("Detay Gör")}</button>
                </li>
              ))}
            </ul>
            {!loading&&!loadError&&evraklar.length===0&&<p className="career-document-empty">{tx('Henüz bir evrak talebiniz bulunmamaktadır.')}</p>}
          </>
        )}

        {detayEvrak && (
          <div className="evrak-detay-popup">
            <div className="evrak-detay-row">
              <span className="etiket">{tx("Evrak Adı :")}</span><span>{catalogLabel('documentName',detayEvrak.ad,lang)}</span>
            </div>
            <div className="evrak-detay-row">
              <span className="etiket">{tx("Açıklama :")}</span><span>{detayEvrak.aciklama}</span>
            </div>
            <div className="evrak-detay-row">
              <span className="etiket">{tx("Talep Tarihi :")}</span><span>{new Date(detayEvrak.talepTarihi).toLocaleDateString(lang)}</span>
            </div>
            <div className="evrak-detay-row">
              <span className="etiket">{tx("Son Teslim Tarihi :")}</span><span>{new Date(detayEvrak.sonTeslimTarihi).toLocaleDateString(lang)}</span>
            </div>

            <div className="evrak-detay-row">
              <span className="etiket">{tx("Seçilen Dosya :")}</span>
              <span>{previewName || tx("Henüz dosya seçilmedi.")}</span>
            </div>

            {/* ✅ Yüklenen dosya varsa "Dosyayı Gör" butonu göster */}
            {detayEvrak.yuklemeDurumu === "Yüklendi" && (
              <div className="evrak-detay-row">
                <button
                  type="button"
                  className="evrak-dosya-gor-btn"
                  onClick={async () => {
                    try {
                      const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/api/Evrak/dosya?id=${detayEvrak.evrakId}`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                          responseType: "blob", // 🔹 blob şart
                        }
                      );

                      const contentType = response.headers["content-type"]; // 🔹 tipi al
                      const blob = new Blob([response.data], { type: contentType });
                      const url = window.URL.createObjectURL(blob);
                      window.open(url, "_blank");
                    } catch (error) {
                      setFeedback({literal:'❌ Dosya görüntülenemedi!',type:'error'});
                    }
                  }}
                >{tx("Dosyayı Gör")}</button>
              </div>
            )}

            <div className="evrak-upload-row">
              <label
                className="evrak-upload-btn"
                style={{
                  opacity: evrakOnaylandi||isUploading||checking ? 0.5 : 1,
                  cursor: evrakOnaylandi||isUploading||checking ? "not-allowed" : "pointer",
                }}
              >{tx("Dosya Seç")}<input
                  type="file"
                  accept={DOCUMENT_ACCEPT}
                  className="evrak-file-input"
                  onChange={handleFileSelect}
                  disabled={evrakOnaylandi||isUploading||checking}
                />
              </label>

              <button
                type="button"
                className="evrak-detay-yukle-btn"
                onClick={handleUpload}
                disabled={!selectedFile || evrakOnaylandi || isUploading||checking}
              >
                {isUploading ? t('loading') : feedback?.type==='error'||needsCheck ? t('retry') : tx("Evrak Yükle")}
              </button>

            </div>
            <p className="evrak-format-note">{t('uploadFormats')}{selectedFile&&<span> · {(selectedFile.size/1024/1024).toLocaleString(lang,{maximumFractionDigits:2})} MB</span>}</p>
            {isUploading&&<div className="evrak-progress" role="status"><p>{t(progress===100?'uploadProcessing':'uploadSending')} {progress!==null&&progress<100&&<strong>{progress}%</strong>}</p><progress max="100" value={progress===null?undefined:progress} aria-label={t('uploadProgressLabel')}/><button type="button" onClick={()=>{if(uploadRef.current){uploadRef.current.reason='cancelled';uploadRef.current.controller.abort();}}}>{t('uploadStop')}</button></div>}
            {needsCheck&&<button type="button" className="evrak-status-check" disabled={isUploading||checking} onClick={checkStatus}>{t(checking?'uploadChecking':'uploadCheck')}</button>}

            <button
              type="button"
              className="evrak-detay-kapat-btn"
              disabled={isUploading||checking}
              onClick={closeDetail}
            >{tx("Geri")}</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default EvrakPopup;
